/**
 * Extracts a human-readable string from any API error shape.
 *
 * Handles:
 *  - axios errors (err.response.data.detail / err.response.data.error)
 *  - Pydantic v2 validation errors (array of {loc, msg, type})
 *  - our normalized backend payload ({detail: string, errors: string[]})
 *  - plain Error instances / strings / undefined
 *
 * Always returns a STRING. Never an object. Safe to pass to toast(), setError(),
 * alert() or render inside JSX.
 */
export function extractErrorMessage(err, fallback = "Une erreur est survenue") {
  if (!err) return fallback;

  // Plain string thrown
  if (typeof err === "string") return err;

  // Axios-style error → look at response.data
  const data = err?.response?.data ?? err?.data ?? err;

  // Our normalized payload: { detail: "msg · msg" }
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }

  // Legacy Pydantic v2: detail is an array of {loc, msg, type}
  if (Array.isArray(data?.detail)) {
    return data.detail
      .map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc)
          ? d.loc.filter((p) => p !== "body" && p !== "query" && p !== "path").join(" → ")
          : "";
        const msg = d?.msg || "valeur invalide";
        return field ? `${field} : ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" · ") || fallback;
  }

  // Our normalized `errors` array
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.filter((s) => typeof s === "string").join(" · ") || fallback;
  }

  // Generic `error` / `message` fields
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;

  return fallback;
}

export default extractErrorMessage;
